// Result-set 1: one row per stand from MILL.GetTrendDisplayDataWeb
export interface StandRow {
	Stand: string;
	Active: boolean | null;
	RFactorCurrent: number | null;
	RFactorTarget: number | null;
	RFactorMin: number | null;
	RFactorMax: number | null;
	PassCount: number | null;
	PassMaxCount: number | null;
	Current: number | null;
	IsBilletPresent: boolean | null;
	Speed: number | null;
	Gap: number | null;
	EnabledComparison: boolean | null;
	// Canonical 1..18 position used by MILL.BilletInStands and
	// MILL.GetBilletInStandTagsId() (NOT catStands.Id — which is
	// non-sequential). The frontend uses this to translate the
	// [first, last] range returned in InFlightBillet back to its filtered
	// visible-stand index space.
	PhysicalPosition: number | null;
}

// Result-set 2: current campaign banner (from EXEC MILL.GetCampaignsMeta @id = -1)
export interface CampaignBanner {
	Id: number;
	RecipeId: number | null;
	RecipeName: string | null;
	ProductSizeName: string | null;
	ProductTypeName: string | null;
}

// Result-set 3: one row per billet currently in flight. The SP computes the
// stand range deterministically (each billet's stripe between its tail and
// the next-older billet's tail, intersected with HIGH HMD stands). Frontend
// renders ONE capsule per row — no bucketing, no inheritance, no bridging.
export interface InFlightBillet {
	BilletId: number;
	HeatNumber: string | null;
	// Canonical 1..18 positions — match StandRow.PhysicalPosition.
	FirstStandPosition: number;
	LastStandPosition: number;
	// 1 when the tail was derived from a BIS row (billet has exited at least
	// one stand). 0 when the billet is so fresh nothing has been written yet.
	TailExited: boolean;
}

export interface TrendDisplayData {
	stands: StandRow[];
	banner: CampaignBanner | null;
	inFlightBillets: InFlightBillet[];
}

// Property-row meta for the transposed grid (1 row per property × N stand columns)
export type GridPropertyKey =
	| "recipe"
	| "actual"
	| "max"
	| "min"
	| "passCount"
	| "passMaxCount"
	| "speed"
	| "current"
	| "billetPresent"
	| "gap";

export type GridPropertyFormat = "FLOAT3" | "INT" | "BOOL";

export interface GridPropertyDef {
	key: GridPropertyKey;
	label: string;
	// Short helper text shown as the HTML `title` on the property cell —
	// hover-explanation of what the row represents, kept terse.
	description: string;
	standField: keyof StandRow;
	format: GridPropertyFormat;
	legendColor?: string; // small dot before the property label (Recipe / Actual / Max / Min)
}

// Display order matches the legacy screen exactly.
export const GRID_PROPERTIES: readonly GridPropertyDef[] = [
	{
		key: "recipe",
		label: "Recipe",
		description: "Target R-Factor for this stand from the active recipe",
		standField: "RFactorTarget",
		format: "FLOAT3",
		legendColor: "#2A32FF", // legacy "target" blue
	},
	{
		key: "actual",
		label: "Actual",
		description: "Live R-Factor reading from the stand drive",
		standField: "RFactorCurrent",
		format: "FLOAT3",
		legendColor: "#244200", // legacy "actual" dark green
	},
	{
		key: "max",
		label: "Max",
		description: "Upper R-Factor bound — alarm if the actual exceeds it",
		standField: "RFactorMax",
		format: "FLOAT3",
		legendColor: "#E60000", // legacy "max/min" red
	},
	{
		key: "min",
		label: "Min",
		description: "Lower R-Factor bound — alarm if the actual drops below",
		standField: "RFactorMin",
		format: "FLOAT3",
		legendColor: "#E60000",
	},
	{
		key: "passCount",
		label: "Pass Count",
		description: "Number of bars rolled by this stand in the current campaign",
		standField: "PassCount",
		format: "INT",
	},
	{
		key: "passMaxCount",
		label: "Change at",
		description: "Pass-count threshold at which the rollset must be changed",
		standField: "PassMaxCount",
		format: "INT",
	},
	{
		key: "speed",
		label: "Speed",
		description: "Roll-tangential speed (m/s)",
		standField: "Speed",
		format: "FLOAT3",
	},
	{
		key: "current",
		label: "Current",
		description:
			"Stand motor current draw (amperes) — proxy for mechanical load",
		standField: "Current",
		format: "FLOAT3",
	},
	{
		key: "billetPresent",
		label: "Billet Present",
		description: "Hot Metal Detector — true while a billet is in the stand",
		standField: "IsBilletPresent",
		format: "BOOL",
	},
	{
		key: "gap",
		label: "Gap",
		description:
			"Physical gap between upper and lower roll (mm). Mapped only on stands 1 and 17.",
		standField: "Gap",
		format: "FLOAT3",
	},
] as const;
